import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Paginator } from 'primereact/paginator'; // **** 1. Import Paginator แยกออกมา ****
import { Galleria } from 'primereact/galleria';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShare } from '@fortawesome/free-solid-svg-icons';
import { faFacebook } from '@fortawesome/free-brands-svg-icons';
import ShareDialogContent from './ShareDialogContent';
import styles from '../../styles/BlogviewTTT.module.scss';

const BlogviewTTT = ({
  headers,
  totalCount,
  searchQuery,
  pageLength,
  currentPage,
  setCurrentPage, // ฟังก์ชันสำหรับเปลี่ยนหน้าจาก Parent
  handlePageLengthChange,
}) => {
  // **** 2. ลด State ที่ไม่จำเป็นออกไป ****
  // ไม่ต้องมี filteredHeaders แล้ว เราจะกรองข้อมูลก่อนส่งให้ DataTable เลย
  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedShareData, setSelectedShareData] = useState(null);
  const [shareStatus, setShareStatus] = useState({});

  // **** 3. คำนวณข้อมูลที่จะแสดงผลโดยตรง ไม่ต้องใช้ useEffect ****
  const filteredData = React.useMemo(() => {
    if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      return headers.filter((item) =>
        item.post_title.toLowerCase().includes(lowercasedQuery) ||
        (item.post_content && item.post_content.toLowerCase().includes(lowercasedQuery))
      );
    }
    return headers; // ถ้าไม่มี search query ก็ใช้ข้อมูลทั้งหมด
  }, [headers, searchQuery]);


  // Logic ส่วนที่เหลือเหมือนเดิม (extractTextFromHTML, dateTemplate, etc.)
  // ... (โค้ด fetchShareStatus และ useEffect ของมันยังคงเดิม) ...
  const extractTextFromHTML = (html) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html || '';
    const text = tempDiv.textContent || tempDiv.innerText || '';
    return text.length > 100 ? text.slice(0, 100) + '...' : text;
  };

  const dateTemplate = (rowData) => {
    if (rowData.post_modified) {
      try {
        return new Date(rowData.post_modified).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
      } catch {
        return 'N/A';
      }
    }
    return 'N/A';
  };

  const fetchShareStatus = async (id, retries = 2) => {
    for (let attempt = 1; attempt <= retries + 1; attempt++) {
      try {
        const response = await fetch(`https://py.afaa.website/share/api/view/?id_connection=${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data.data || [];
      } catch (error) {
        if (attempt <= retries) {
          console.warn(`Retry ${attempt} for ID ${id}:`, error.message);
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt)); // Exponential backoff
          continue;
        }
        console.error(`Error fetching share status for ID ${id} after ${retries + 1} attempts:`, error);
        return [];
      }
    }
  };

  useEffect(() => {
    const loadShareStatuses = async () => {
      setShareStatus({}); // Reset status ก่อนโหลดใหม่
      const newStatusMap = {};
      for (const item of headers) {
        newStatusMap[item.ID] = { loading: true, count: 0 };
      }
      setShareStatus(newStatusMap);

      const fetchPromises = headers.map(async (item) => {
        const shareData = await fetchShareStatus(item.ID);
        setShareStatus(prev => ({
          ...prev,
          [item.ID]: { count: shareData.length, loading: false }
        }));
      });
      await Promise.all(fetchPromises);
    };

    if (headers && headers.length > 0) {
      loadShareStatuses();
    }
  }, [headers]);
  // ------------------------------------------------------------------------

  // **** 4. จัดการ Pagination Event จาก Paginator ****
  const onPageEvent = (event) => {
    // `event.page` คือหน้าที่ผู้ใช้คลิก (เริ่มจาก 0)
    // เราต้องบวก 1 เพื่อให้ตรงกับ `currentPage` ของเรา (ที่เริ่มจาก 1)
    setCurrentPage(event.page + 1);
  };
  
  // Logic การแสดงผลอื่นๆ ยังคงเดิม
  const handleShareClick = (postData) => {
    setSelectedPost(postData);
    setSelectedShareData(null);
    setIsDialogVisible(true);
  };

  const handleShareStatusClick = async (rowData) => {
    const statusEntry = shareStatus[rowData.ID];
    if (statusEntry && statusEntry.count > 0 && !statusEntry.loading) {
      setIsDialogVisible(true);
      setSelectedPost(rowData);
      setSelectedShareData(null);
      try {
        const shareDataArray = await fetchShareStatus(rowData.ID);
        if (shareDataArray.length > 0) {
          setSelectedShareData(shareDataArray[0]);
        } else {
          setSelectedShareData({ error: "No share details found." });
        }
      } catch (error) {
        setSelectedShareData({ error: "Failed to load share details." });
      }
    }
  };

  const imageTemplate = (rowData) => {
    if (!rowData.images || rowData.images.length === 0) {
      return 'No Image';
    }
    if (rowData.images.length === 1) {
      return (
        <img
          src={rowData.images[0].guid}
          alt={rowData.images[0].post_title}
          className={styles.singleImage}
        />
      );
    }
    const galleriaItemTemplate = (item) => (
      <img src={item.guid} alt={item.post_title} style={{ width: '100%', display: 'block' }} />
    );
    return (
      <div className={styles.galleriaContainer}>
        <Galleria
          value={rowData.images}
          item={galleriaItemTemplate}
          numVisible={1}
          showThumbnails={false}
          showIndicators
          circular
          autoPlay={false}
          transitionInterval={3000}
        />
      </div>
    );
  };

  const contentTemplate = (rowData) => extractTextFromHTML(rowData.post_content);

  const postUrlTemplate = (rowData) => {
    if (!rowData.post_name) {
      return 'N/A';
    }
    const url = `https://blog.ttt-website.com/${rowData.post_name}`;
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        View Post
      </a>
    );
  };

  const shareTemplate = (rowData) => (
    <Button
      icon={<FontAwesomeIcon icon={faFacebook} />}
      className={`${styles.shareButton} p-button-text p-button-base`}
      onClick={() => handleShareClick(rowData)}
      tooltip="Share on Facebook"
      tooltipOptions={{ position: 'top' }}
    />
  );

  const shareStatusTemplate = (rowData) => {
    const statusEntry = shareStatus[rowData.ID];

    if (!statusEntry || statusEntry.loading) {
      return (
        <div className={styles.spinnerContainer}>
          <ProgressSpinner style={{ width: '25px', height: '25px' }} strokeWidth="6" animationDuration=".8s" />
        </div>
      );
    }

    const shareCount = statusEntry.count;

    if (shareCount > 0) {
      const icons = [];
      for (let i = 0; i < Math.min(shareCount, 5); i++) {
        icons.push(
          <FontAwesomeIcon
            key={i}
            icon={faShare}
            className={styles.shared}
            onClick={() => handleShareStatusClick(rowData)}
            style={{ marginRight: i < Math.min(shareCount, 5) - 1 ? '5px' : '0' }}
            title={`View share details (${shareCount})`}
          />
        );
      }
      return <div>{icons}</div>;
    } else {
      return (
        <div>
          <FontAwesomeIcon
            icon={faShare}
            className={styles.shareStatusIcon}
            title="Not shared"
          />
        </div>
      );
    }
  };
  
  const tableFooter = (
    <div className={styles.tableFooter}>
      <strong>Total Posts:</strong> {totalCount.toLocaleString()}
    </div>
  );

  return (
    <div className={styles.ideaslabHeaderWrapper}>
      <div className="d-grid gap-2 d-md-flex justify-content-md-end mb-2">
        <div className={styles.selectContainer}>
          <select id="pageLengthSelect" className="form-select" value={pageLength} onChange={handlePageLengthChange}>
            <option value="10">10 Rows</option>
            <option value="20">20 Rows</option>
            <option value="50">50 Rows</option>
          </select>
        </div>
      </div>

      {/* **** 5. ปรับปรุง DataTable และ Paginator **** */}
      <DataTable
        value={filteredData} // ใช้ข้อมูลที่กรองแล้วโดยตรง
        className={`${styles.dataTable} p-datatable-base`}
        // **** ใช้ key ที่เปลี่ยนตามหน้าปัจจุบัน เพื่อบังคับให้ React สร้างตารางใหม่ทุกครั้งที่เปลี่ยนหน้า ****
        key={`datatable-${currentPage}`} 
        footer={tableFooter}
        emptyMessage="No data found."
      >
        <Column field="ID" header="ID" sortable className={styles.columnId} />
        <Column field="post_title" header="Title" sortable className={styles.columnTitle} />
        <Column field="post_content" header="Content" body={contentTemplate} className={styles.columnContent} />
        <Column field="post_modified" header="Modified Date" body={dateTemplate} sortable className={styles.columnModifiedDate} />
        <Column field="images" header="Image" body={imageTemplate} className={styles.columnImage} />
        <Column header="Link" body={postUrlTemplate} className={styles.columnLink} />
        <Column header="Share" body={shareTemplate} className={styles.columnShare} />
        <Column header="Share Status" body={shareStatusTemplate} className={styles.columnShareStatus} />
      </DataTable>

      <Paginator
        first={(currentPage - 1) * pageLength}
        rows={pageLength}
        totalRecords={totalCount}
        onPageChange={onPageEvent}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        rowsPerPageOptions={[10, 20, 50]}
      />
      {/* ------------------------------------------- */}

      <Dialog
        header={selectedShareData?.error ? "Error" : (selectedShareData ? 'Share Status Details' : 'Share this Post')}
        visible={isDialogVisible}
        className={styles.dialog}
        onHide={() => setIsDialogVisible(false)}
        footer={<Button label="Close" icon="pi pi-times" onClick={() => setIsDialogVisible(false)} className="p-button-text" />}
        draggable={false}
      >
        <ShareDialogContent
          selectedPost={selectedPost}
          shareData={selectedShareData}
          isReadOnly={!!selectedShareData}
        />
      </Dialog>
    </div>
  );
};

export default BlogviewTTT;